/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

/* eslint-disable jsx-a11y/no-noninteractive-tabindex */

import React from 'react';
import PropTypes from 'prop-types';
import { Icon, StyledModal, Text, Heading, Stack } from 'sarsaparilla';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isEmail from 'validator/es/lib/isEmail';
import AlternatePermitHolderEntry from './AlternatePermitHolderEntry';
import { getClassName } from '../../utils/utils';

const GROUP_DETAIL_ALT_PROP_PATH = 'alt_permit_holders';

export default class AlternatePermitHolder extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            modalIsOpen: false,
        };
        this.closeButtonClick = this.closeButtonClick.bind(this);
    }

    onFormValidate(isInValid, error, path) {
        if (isInValid) {
            this.props.pActions.formValidationChange(
                `${this.props.reservation.id}_${path}`,
                error
            );
        } else {
            this.props.pActions.formValidationChange(
                `${this.props.reservation.id}_${path}`,
                null
            );
        }
    }

    closeModal() {
        this.setState({ modalIsOpen: false });
    }

    closeButtonClick() {
        this.setState({ modalIsOpen: false });
        this.closeModal();
    }

    openModal() {
        this.setState({ modalIsOpen: true });
    }

    addLeader() {
        const newGroupDetailAlt = get(
            this.props.reservation,
            GROUP_DETAIL_ALT_PROP_PATH,
            []
        );
        const currentCount = newGroupDetailAlt.length;
        const condition = currentCount + 1 <= 4;
        if (condition) {
            const newLeader = {
                key: `${this.props.reservation.id}.${currentCount}`,
            };
            newGroupDetailAlt.push(newLeader);
        }
        this.props.pActions.updatePendingReservation(
            this.props.reservation.id,
            GROUP_DETAIL_ALT_PROP_PATH,
            newGroupDetailAlt
        );
    }

    removeLeader(index) {
        const newGroupDetailAlt = get(
            this.props.reservation,
            GROUP_DETAIL_ALT_PROP_PATH,
            []
        );
        if (newGroupDetailAlt.length > 0) {
            newGroupDetailAlt.splice(index, 1);
            this.props.pActions.updatePendingReservation(
                this.props.reservation.id,
                GROUP_DETAIL_ALT_PROP_PATH,
                newGroupDetailAlt
            );
        }
    }

    handleAlternateLeaderValidation(index, isInValid, error) {
        const path = `${GROUP_DETAIL_ALT_PROP_PATH}[${index}].email`;
        if (isInValid) {
            this.props.pActions.formValidationChange(
                `${this.props.reservation.id}_${path}`,
                error
            );
        } else {
            this.props.pActions.formValidationChange(
                `${this.props.reservation.id}_${path}`,
                null
            );
        }
    }

    alternateLeaderValidatorMethod(index, value) {
        if (isEmpty(value)) {
            return false;
        }

        if (!isEmail(value)) {
            return true;
        }

        return (
            isEqual(
                get(this.props.reservation, 'primary_permit_holder.email', ''),
                value
            ) ||
            get(this.props.reservation, GROUP_DETAIL_ALT_PROP_PATH, []).filter((alt) =>
                isEqual(alt.email, value)
            ).length > 1
        );
    }

    renderAltLeaders() {
        const { pActions, reservation, displayValidationError, validation } = this.props;
        const { id } = reservation;
        const groupDetailAlt = get(
            this.props.reservation,
            GROUP_DETAIL_ALT_PROP_PATH,
            []
        );
        if (groupDetailAlt.length === 0) {
            return null;
        }

        return groupDetailAlt.map((leader, index) => {
            const path = `${GROUP_DETAIL_ALT_PROP_PATH}[${index}].email`;
            const className = getClassName(
                get(validation, `${id}_${path}`),
                displayValidationError
            );
            return (
                <AlternatePermitHolderEntry
                    key={leader.key || leader.email}
                    leaderId={leader.key}
                    leader={leader}
                    index={index}
                    handleAlternateLeaderChange={(ind, key, value) =>
                        pActions.updatePendingReservation(
                            id,
                            `${GROUP_DETAIL_ALT_PROP_PATH}[${ind}].email`,
                            value
                        )
                    }
                    handleAlternateLeaderValidation={this.handleAlternateLeaderValidation.bind(
                        this,
                        index
                    )}
                    alternateLeaderValidatorMethod={this.alternateLeaderValidatorMethod.bind(
                        this,
                        index
                    )}
                    className={className}
                    removeLeader={(ind) => this.removeLeader(ind)}
                />
            );
        });
    }

    render() {
        const modalSections = [
            {
                title: 'Why An Alternate Permit Holder?',
                paragraph:
                    'The permit holder is required to be present on the entire trip for this permit to be valid. In the event that the permit holder is unavailable, alternate permit holders can be designated at the time of reservation.',
            },
            {
                title: 'How to Add an Alternate Permit Holder',
                paragraph:
                    'Enter an email address for an alternate permit holder. The alternate permit holder must respond and accept being an alternate within 72 hours or this permit will not have an alternate permit holder. If the alternate permit holder does not have a recreation.gov account, they will be required to create an account.',
            },
            {
                title: 'Alternate Permit Holder Considerations',
                paragraph:
                    'Each guest, regardless of age, entering the Serpentine ACEC is required to be listed on the permit and have proof of the permit on their person. An individual is allowed a total of five permits per calendar year.',
            },
        ];
        return (
            <div>
                <div className="leader-container">
                    <StyledModal
                        size="md"
                        isOpen={this.state.modalIsOpen}
                        heading="Alternate Permit Holders"
                        onRequestClose={this.closeButtonClick}
                    >
                        <Stack className="rec-modal-availability-wrap">
                            {modalSections.map((section) => (
                                <div key={section.title}>
                                    <Heading headingLevel={6} appearance="h6">
                                        {section.title}
                                    </Heading>
                                    <Text
                                        tabIndex={0}
                                        role="dialog"
                                        aria-label={section.paragraph}
                                    >
                                        {section.paragraph}
                                    </Text>
                                </div>
                            ))}
                        </Stack>
                    </StyledModal>
                </div>
                <div className="permit-alt-leader">
                    <div className="permit-alt-leader-form">
                        <div className="permit-alt-leader-msg">
                            <Icon iconName="info" /> You may select a max of 4 other
                            people to act as alternate permit holders on your trip. Please
                            enter a valid email address for each of the intended holders.
                            They will receive an email and will need to validate within 72
                            hours. See{' '}
                            <button
                                className="rec-button-link"
                                onClick={() => this.openModal()}
                                type="button"
                            >
                                Alternate Permit Holders
                            </button>{' '}
                            for more information.
                            <br />
                        </div>
                        {this.renderAltLeaders()}
                    </div>
                    <div className="permit-add-icon">
                        <button
                            type="button"
                            className="rec-button-link addOneLeaderLink"
                            onClick={() => this.addLeader()}
                        >
                            <Icon iconName="add-circle" />
                            Add alternate permit holder (4 max)
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}

AlternatePermitHolder.propTypes = {
    validation: PropTypes.object.isRequired,
    reservation: PropTypes.object.isRequired,
    pActions: PropTypes.object.isRequired,
    displayValidationError: PropTypes.bool,
};
